import { useEffect, useMemo, useState } from "react";

import TabsWithPills from "V2.0/common/tabsWithPills";
import {
  Table,
  TableHead,
  TableBody,
  TableHeader,
  TableRow,
  TableCell,
} from "V2.0/common/table";
import { Badge } from "V2.0/common/badge";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

import EvalRunResultItems from "../EvalRunResult/EvalRunResultItems";
import EvalRunResultMessages from "../EvalRunResult/EvalRunResultConversation/EvalRunResultMessages";
import EvalRunReportSummary from "../EvalRunReport/EvalRunReportSummary";
import { EvalEvent, EvalEventLabels } from "../types";

export default EvalRunUnified;

/**
 * The run detail screen — one screen, one rail.
 *
 * This was one of two proposed layouts, picked over the other; "unified" is the
 * name that stuck. It replaced the old Result and Report tabs, which are still
 * on disk under EvalRunResult/index and EvalRunReport/index but are no longer
 * rendered by anything.
 *
 * Those tabs built the same rail three times — Result, Report → "Score Details",
 * Report → "Event Analysis" — each with its own independent selection, so
 * picking a model x persona in one place and switching tabs lost it.
 *
 * Here the rail is built once and owns the selection. The right panel switches
 * between Conversation / Scores / Events for whichever row is active, and the
 * run-level summary is its own view above the rail.
 */

const Panel = {
  CONVERSATION: "Conversation",
  SCORES: "Scores",
  EVENTS: "Events",
};

function EvalRunUnified(props) {
  const { evalRun } = props;

  const [selectedResult, setSelectedResult] = useState(undefined);
  const [activePanel, setActivePanel] = useState(Panel.CONVERSATION);
  const [showSummary, setShowSummary] = useState(true);

  const personaRegistry = useMemo(() => {
    return (evalRun?.config?.personas ?? []).reduce((acc, persona) => {
      return { ...acc, [persona.name]: persona };
    }, {});
  }, [evalRun]);

  // Land on the summary, since "how did the run go" comes before "what did this
  // one simulation say".
  useEffect(() => {
    setShowSummary(true);
  }, [evalRun]);

  const tabs = useMemo(() => {
    return Object.values(Panel).map((panel) => ({
      name: panel,
      current: panel === activePanel,
      href: "#",
    }));
  }, [activePanel]);

  const persona = personaRegistry[selectedResult?.persona];

  function handleSelectResult(result) {
    setSelectedResult(result);
    setShowSummary(false);
  }

  return (
    <div className="flex flex-1 flex-col gap-y-4 min-h-0">
      <div className="grid grid-cols-3 lg:grid-cols-4 h-full gap-x-4 min-h-0">
        <div className="col-span-1 flex flex-col gap-y-2 min-h-0">
          {/* Run-level view sits above the simulations rather than behind a
              separate top-level tab. */}
          <button
            type="button"
            onClick={() => setShowSummary(true)}
            className={`shrink-0 rounded-lg border p-3 text-left text-sm font-medium ${
              showSummary
                ? "bg-gray-100 text-gray-900"
                : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            Overall Summary
          </button>

          <EvalRunResultItems
            evalRun={evalRun}
            selectedResult={showSummary ? undefined : selectedResult}
            setSelectedResult={handleSelectResult}
          />
        </div>

        <div className="col-span-2 lg:col-span-3 border rounded-lg h-full flex flex-col overflow-hidden">
          {showSummary ? (
            <div className="flex-1 overflow-auto p-4 flex flex-col gap-y-8">
              <RunOverview evalRun={evalRun} />

              <EvalRunReportSummary
                evalRun={evalRun}
                summary={evalRun?.report?.summary}
                modelRankings={evalRun?.report?.modelRankings ?? []}
              />
            </div>
          ) : (
            <>
              <div className="shrink-0 border-b p-4">
                <TabsWithPills
                  tabs={tabs}
                  onTabChange={(e, { tab }) => setActivePanel(tab.name)}
                />
                {/* Persona detail used to be its own tab beside Messages. It's a
                    single line of context, so it belongs in the header. */}
                {persona && (
                  <div className="mt-3 text-xs text-gray-500">
                    <span className="font-medium text-gray-700">
                      {persona.name}
                    </span>
                    {persona.scenario ? ` · ${persona.scenario}` : ""}
                    {persona.expectedOutput ? (
                      <span className="mt-1 block">
                        Expected: {persona.expectedOutput}
                      </span>
                    ) : null}
                    {/* The values this persona filled the prompt's
                        {placeholders} with, so a score can be read against them. */}
                    {Object.keys(persona.attributes ?? {}).length > 0 ? (
                      <span className="mt-1 block">
                        Attributes:{" "}
                        {Object.entries(persona.attributes)
                          .map(([key, value]) => `${key} = ${value}`)
                          .join(" · ")}
                      </span>
                    ) : null}
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-auto">
                {activePanel === Panel.CONVERSATION && (
                  <EvalRunResultMessages
                    evalRunResultMessages={selectedResult?.conversation ?? []}
                    // Coach guidance events render as a callout under the turn
                    // they were generated in, next to the reply they shaped.
                    evalRunResultEvents={selectedResult?.eventsOccurred ?? []}
                  />
                )}

                {activePanel === Panel.SCORES && (
                  <ScoresPanel scores={selectedResult?.scores} />
                )}

                {activePanel === Panel.EVENTS && (
                  <EventsPanel
                    eventScores={selectedResult?.eventScores}
                    eventsOccurred={selectedResult?.eventsOccurred}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Run-level figures, above the written summary — the number people came for
 * before the paragraph explaining it.
 */
function RunOverview(props) {
  const { evalRun } = props;

  const overallScore =
    typeof evalRun?.overallScore === "number" ? evalRun.overallScore : null;

  return (
    <div>
      <Stat
        label="Overall score"
        value={overallScore === null ? "—" : overallScore.toFixed(1)}
        unit="/10"
      />
    </div>
  );
}

function Stat(props) {
  const { label, value, unit } = props;

  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
        {value}
        {unit && (
          <span className="ml-0.5 text-sm font-normal text-gray-400">
            {unit}
          </span>
        )}
      </p>
    </div>
  );
}

/**
 * Per-criterion scores, including the judge's reasoning. The reasoning was
 * previously only visible in the retired Report → "Score Details" tab; it's
 * here so nothing was lost when that tab went.
 */
function ScoresPanel(props) {
  const { scores } = props;

  if (!scores || scores.length === 0) return <EmptyPanel />;

  return (
    <div className="p-4">
      <Table>
        <TableHead>
          <TableHeader>Criteria</TableHeader>
          <TableHeader>Score</TableHeader>
          <TableHeader>Reasoning</TableHeader>
        </TableHead>
        <TableBody>
          {scores.map((score, idx) => (
            <TableRow key={idx}>
              <TableCell>
                <Badge>{score.criterion}</Badge>
              </TableCell>
              <TableCell>{score.score}</TableCell>
              <TableCell>
                <p className="w-full text-xs text-gray-500 whitespace-pre-line">
                  {score.reasoning}
                </p>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * Every event that fired, plus every event that was asserted.
 *
 * The old Event Analysis read only `eventScores`, which is built solely from
 * `config.expectedEvents` — so a run that asserted nothing showed an empty tab
 * even when the assistant had genuinely transferred the call to a human. That's
 * the single most important thing a run can tell you, and it was hidden.
 *
 * Rows now come from the union, so an event that fired without being asserted
 * still appears, marked as such.
 */
function EventsPanel(props) {
  const { eventScores, eventsOccurred } = props;

  const rows = useMemo(() => {
    const asserted = new Map<string, any>(
      (eventScores ?? []).map((eventScore) => [eventScore.event, eventScore]),
    );

    const fired = new Map<string, any>();
    for (const occurred of eventsOccurred ?? []) {
      // Keep the first occurrence — that's the turn it was triggered on.
      if (!fired.has(occurred.event)) fired.set(occurred.event, occurred);
    }

    const names = new Set([...asserted.keys(), ...fired.keys()]);

    return Array.from(names).map((name) => {
      const assertion = asserted.get(name);
      const occurrence = fired.get(name);

      return {
        event: name,
        occurred: Boolean(occurrence) || Boolean(assertion?.occurred),
        expected: assertion ? assertion.expected : null,
        turn: occurrence?.turn ?? assertion?.turn ?? null,
      };
    });
  }, [eventScores, eventsOccurred]);

  // Coach guidance is the one event whose payload is something a reviewer reads:
  // the advice the coach gave. It can fire on several turns while the table above
  // keeps one row per event, so every occurrence is listed here with its turn.
  const coachGuidanceEvents = useMemo(() => {
    return (eventsOccurred ?? []).filter(
      (occurred) =>
        occurred.event === EvalEvent.COACH_GUIDANCE &&
        Boolean(occurred.details?.guidance),
    );
  }, [eventsOccurred]);

  if (rows.length === 0) return <EmptyPanel />;

  return (
    <div className="p-4 flex flex-col gap-y-6">
      <Table>
        <TableHead>
          <TableHeader>Event</TableHeader>
          <TableHeader>Turn</TableHeader>
          <TableHeader>Happened</TableHeader>
          <TableHeader>Expected</TableHeader>
          {/* No Details column. It printed the raw event payload — internal
              shapes like {"actionName":"Request Disconnect"} — which tells a
              customer nothing they can act on and made every row tall. */}
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => {
            const expected = wasExpected(row);

            return (
            <TableRow key={idx}>
              <TableCell>
                <Badge>{EvalEventLabels[row.event] ?? row.event}</Badge>
              </TableCell>
              <TableCell>{row.turn ?? "—"}</TableCell>
              <TableCell>
                <Badge color={row.occurred ? "emerald" : "zinc"}>
                  {row.occurred ? "Yes" : "No"}
                </Badge>
              </TableCell>
              {/* "Expected" answers "was this what you expected?" — not "did you
                  assert it should happen". Reading the raw assertion made
                  "Expected: no" look like it meant the event shouldn't occur. */}
              <TableCell>
                <Badge color={expected.color}>{expected.label}</Badge>
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {coachGuidanceEvents.length > 0 && (
        <CoachGuidanceList events={coachGuidanceEvents} />
      )}
    </div>
  );
}

/**
 * What the coach told the assistant, per turn. Model and KB-search count sit in
 * the heading; the guidance itself is prose, so it is shown as text rather than
 * as the raw payload the retired Details column used to print.
 */
function CoachGuidanceList(props) {
  const { events } = props;

  return (
    <div className="flex flex-col gap-y-3">
      <p className="text-xs uppercase tracking-wide text-gray-500">
        Coach guidance
      </p>
      {events.map((event, idx) => {
        const { guidance, model, kbSearches } = event.details ?? {};

        return (
          <div
            key={idx}
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2"
          >
            <p className="text-xs font-semibold text-amber-800 mb-1">
              Turn {event.turn}
              {model ? (
                <span className="font-normal text-amber-700"> · {model}</span>
              ) : null}
              {Number.isFinite(kbSearches) && kbSearches > 0 ? (
                <span className="font-normal text-amber-700">
                  {" "}
                  · {kbSearches} KB search{kbSearches === 1 ? "" : "es"}
                </span>
              ) : null}
            </p>
            <p className="text-sm text-gray-800 whitespace-pre-wrap">
              {guidance}
            </p>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Did the run match what you asked for?
 *
 * Note this is NOT the raw `expected` flag. That flag records what you asserted
 * (`false` = "this should not occur"), which reads as "not expected" and made
 * a correct result look like a failure. Here Yes means the outcome matched your
 * assertion, whichever way round it was.
 */
function wasExpected(row) {
  if (row.expected === null) {
    return { label: "Not checked", color: "zinc" };
  }

  return row.occurred === row.expected
    ? { label: "Yes", color: "emerald" }
    : { label: "No", color: "red" };
}


function EmptyPanel() {
  return (
    <div className="flex flex-1 h-full items-center justify-center gap-x-2 p-8">
      <InformationCircleIcon className="size-4 text-gray-700" />
      <p className="text-gray-700 text-md">No data</p>
    </div>
  );
}
